/**
 * Textfeld mit `@`-Erwähnungen — wie in Jira.
 *
 * Ein `@` öffnet die Liste der Mitarbeiter, Tippen filtert sie, Pfeiltasten und
 * Enter wählen aus. Eingesetzt wird der Name als gewöhnlicher Text; wen es
 * betrifft, leitet `erwaehnteKonten` daraus ab (siehe `lib/testing/erwaehnungen`).
 *
 * Bewusst ein `<textarea>` und kein `contenteditable`: Der Text ist Text, geht so
 * unverändert in eine Mail und in den Verlauf, und Einfügen aus der Zwischenablage
 * bleibt das, was der Browser ohnehin richtig macht. Ein Editor mit Chips wäre
 * schöner und würde beim ersten Strg+V unterschiedlich aussehen.
 *
 * Tastatur: ↑ ↓ wählen, Enter oder Tab übernehmen, Esc schliesst. Solange die
 * Liste offen ist, geht Enter **nicht** an das Formular — sonst schickt der
 * Griff zur Auswahl den halben Kommentar ab.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { TICKET_STATUS_LABEL, type TicketStatus } from '../../lib/api/testing';
import { verweisMuster, type TicketVerweis } from '../../lib/testing/ticketverweise';
import {
  angefangeneErwaehnung,
  einsetzen,
  passendePersonen,
  zerlegen,
  type AngefangeneErwaehnung,
  type ErwaehnbarePerson,
} from '../../lib/testing/erwaehnungen';

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (wert: string) => void;
  /** Wer erwähnt werden kann. Leer = das Feld verhält sich wie ein Textarea. */
  personen: ErwaehnbarePerson[];
};

export const MentionTextarea = forwardRef<HTMLTextAreaElement, Props>(
  ({ value, onChange, personen, className, onKeyDown, ...rest }, aussen) => {
    const feld = useRef<HTMLTextAreaElement | null>(null);
    useImperativeHandle(aussen, () => feld.current as HTMLTextAreaElement);

    const [stelle, setStelle] = useState<AngefangeneErwaehnung | null>(null);
    const [gewaehlt, setGewaehlt] = useState(0);
    const vorschlaege = stelle ? passendePersonen(personen, stelle.suche) : [];
    const offen = stelle !== null && vorschlaege.length > 0;

    // Der Cursor steht nach dem Einsetzen nicht am Ende: Er muss von Hand
    // gesetzt werden, und zwar erst, wenn React den neuen Wert gerendert hat.
    const cursorDanach = useRef<number | null>(null);
    useEffect(() => {
      if (cursorDanach.current === null || !feld.current) return;
      feld.current.setSelectionRange(cursorDanach.current, cursorDanach.current);
      cursorDanach.current = null;
    }, [value]);

    const pruefeStelle = useCallback(
      (text: string, cursor: number) => {
        if (personen.length === 0) {
          setStelle(null);
          return;
        }
        setStelle(angefangeneErwaehnung(text, cursor));
        setGewaehlt(0);
      },
      [personen.length]
    );

    const geaendert = (ereignis: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(ereignis.target.value);
      pruefeStelle(ereignis.target.value, ereignis.target.selectionStart ?? 0);
    };

    const uebernehmen = (person: ErwaehnbarePerson) => {
      if (!stelle) return;
      const neu = einsetzen(value, stelle, person);
      cursorDanach.current = neu.cursor;
      onChange(neu.text);
      setStelle(null);
      feld.current?.focus();
    };

    const taste = (ereignis: KeyboardEvent<HTMLTextAreaElement>) => {
      if (offen) {
        if (ereignis.key === 'ArrowDown' || ereignis.key === 'ArrowUp') {
          ereignis.preventDefault();
          const richtung = ereignis.key === 'ArrowDown' ? 1 : -1;
          setGewaehlt(
            (vorher) => (vorher + richtung + vorschlaege.length) % vorschlaege.length
          );
          return;
        }
        if (ereignis.key === 'Enter' || ereignis.key === 'Tab') {
          // Das ist der wichtigste Handgriff hier: Ohne dieses
          // preventDefault schickt Enter den Kommentar ab, statt den Namen
          // einzusetzen.
          ereignis.preventDefault();
          uebernehmen(vorschlaege[gewaehlt] ?? vorschlaege[0]);
          return;
        }
        if (ereignis.key === 'Escape') {
          ereignis.preventDefault();
          setStelle(null);
          return;
        }
      }
      onKeyDown?.(ereignis);
    };

    return (
      <div className="relative">
        <textarea
          {...rest}
          ref={feld}
          value={value}
          onChange={geaendert}
          onKeyDown={taste}
          // Mit der Maus oder den Pfeiltasten woandershin: Die angefangene
          // Erwähnung gilt dann nicht mehr für die neue Stelle.
          onClick={(ereignis) =>
            pruefeStelle(value, ereignis.currentTarget.selectionStart ?? 0)
          }
          onBlur={(ereignis) => {
            // Ein Klick auf einen Vorschlag nimmt dem Feld den Fokus. Erst im
            // nächsten Takt schliessen, sonst ist der Vorschlag weg, bevor er
            // ankommt.
            window.setTimeout(() => setStelle(null), 120);
            rest.onBlur?.(ereignis);
          }}
          className={cn(
            'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
            'ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        />

        {offen && (
          <ul
            role="listbox"
            aria-label="Person erwähnen"
            className="absolute z-50 mt-1 max-h-56 w-full max-w-xs overflow-auto rounded-md border bg-popover p-1 shadow-md"
          >
            {vorschlaege.map((person, nr) => (
              <li key={person.accountId}>
                <button
                  type="button"
                  role="option"
                  aria-selected={nr === gewaehlt}
                  // Nicht onClick: Der Blur des Textfelds käme zuerst und würde
                  // die Liste schliessen, bevor der Klick ankommt.
                  onMouseDown={(ereignis) => {
                    ereignis.preventDefault();
                    uebernehmen(person);
                  }}
                  onMouseEnter={() => setGewaehlt(nr)}
                  className={cn(
                    'w-full rounded-sm px-2 py-1.5 text-left text-sm',
                    nr === gewaehlt ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                  )}
                >
                  {person.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);
MentionTextarea.displayName = 'MentionTextarea';

/** Denselben Text anzeigen, mit hervorgehobenen Erwähnungen. */
/**
 * Text anzeigen, mit hervorgehobenen Erwähnungen und verlinkten Ticketnummern.
 *
 * Die Zerlegung steht in `lib/testing/erwaehnungen`, damit sie ohne React
 * prüfbar ist — und damit Anzeige und Ableitung dieselbe Regel benutzen.
 *
 * `verweise` kommt von aussen und wird für die ganze Seite **einmal** geholt.
 * Wird es nicht übergeben, bleibt eine Nummer gewöhnlicher Text: Ein Link, der
 * ins Leere führt, ist schlechter als keiner.
 */
export type ErwaehnungsOptionen = {
  personen: ErwaehnbarePerson[];
  /** Aufgelöste Ticketnummern — Schlüssel ist die Nummer. */
  verweise?: Map<number, TicketVerweis>;
  /**
   * Präfix des eigenen Nummernkreises (`T` oder `L`). Kommt vom Server
   * (`/testing/status`), nicht aus den aufgelösten Verweisen: Aus denen liesse
   * es sich nur erraten, und bei einer leeren Antwort gar nicht.
   */
  praefix?: string;
  /** Die eigene Nummer. Ein Verweis auf die Seite, auf der man steht, bleibt Text. */
  ohneTicket?: number;
};

export function MentionText({
  text,
  className,
  ...optionen
}: ErwaehnungsOptionen & { text: string; className?: string }) {
  return (
    <p className={cn('whitespace-pre-wrap', className)}>
      <MentionInline text={text} {...optionen} />
    </p>
  );
}

/**
 * Nur die Stücke eines Textes — ohne umgebenden Absatz. Damit hebt auch die
 * Markdown-Anzeige (`markdown-text`) Erwähnungen und Verweise hervor.
 */
export function MentionInline({
  text,
  personen,
  verweise,
  praefix,
  ohneTicket,
}: ErwaehnungsOptionen & { text: string }) {
  const stuecke = zerlegen(
    text,
    personen,
    verweise && verweise.size > 0
      ? {
          muster: verweisMuster(praefix ?? 'T'),
          vorhanden: (nummer) => nummer !== ohneTicket && verweise.has(nummer),
        }
      : undefined
  );

  return (
    <>
      {stuecke.map((stueck, nr) => {
        if (stueck.ticket !== undefined) {
          const ziel = verweise?.get(stueck.ticket);
          return ziel ? (
            <TicketVerweisLink key={nr} text={stueck.text} ticket={ziel} />
          ) : (
            <span key={nr}>{stueck.text}</span>
          );
        }
        if (stueck.erwaehnt) {
          return (
            <span key={nr} className="rounded bg-accent/60 px-1 font-medium text-foreground">
              {stueck.text}
            </span>
          );
        }
        return <span key={nr}>{stueck.text}</span>;
      })}
    </>
  );
}

/**
 * Ein Verweis auf ein anderes Ticket: Link plus Tooltip mit Titel, Stand und
 * Auszug.
 *
 * Der Tooltip steht sofort, weil die Daten schon da sind — geholt wird für die
 * ganze Seite in einem Aufruf, nicht beim Überfahren. Ein Tooltip, der erst
 * nachlädt, ist bei einem Verweis mitten im Satz nutzlos: Bis er steht, ist die
 * Maus weiter.
 */
function TicketVerweisLink({ text, ticket }: { text: string; ticket: TicketVerweis }) {
  return (
    // Eigener Provider je Verweis: Einen globalen gibt es in dieser App nicht,
    // und ein Leaf, das nur in einer bestimmten Umgebung funktioniert, geht beim
    // nächsten Einsatzort still kaputt. Der Provider kostet nichts.
    // 200ms statt der voreingestellten 700: Bei einem Verweis mitten im Satz
    // wartet niemand eine Dreiviertelsekunde.
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={`/testing/${ticket.id}`}
            className="font-mono text-sm font-medium text-primary underline decoration-dotted underline-offset-2 hover:decoration-solid"
          >
            {text}
          </Link>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm space-y-1 px-3 py-2">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-[11px] text-muted-foreground">{ticket.label}</span>
            <span className="text-xs text-muted-foreground">
              {TICKET_STATUS_LABEL[ticket.status as TicketStatus] ?? ticket.status}
            </span>
          </div>
          <div className="text-xs font-medium leading-snug text-popover-foreground">
            {ticket.title}
          </div>
          {ticket.auszug && (
            <div className="text-[11px] leading-snug text-muted-foreground">{ticket.auszug}</div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
